import axios from 'axios';
import { recoverFromAdminAuthFailure } from '../utils/adminAuthFailure';

// =========================
// 관리자 API 주소
// =========================

const BASE_URL =
    '/api/admin';

// =========================
// axios instance
// =========================

const adminApi = axios.create({
    baseURL: BASE_URL,
    headers: {
        'Content-Type':
            'application/json'
    }
});

export interface AnalyticsMetric { name: string; value: number; detail?: string; visitors?: number; }
export interface AnalyticsDailyPoint { label: string; pageViews: number; visitors: number; sessions: number; }
export interface AnalyticsRecentVisit { id: number; visitedAt: string; visitor: string; path: string; device: string; browser: string; operatingSystem: string; referrer: string; }
export interface AnalyticsDashboardData {
    days: number;
    generatedAt: string;
    summary: { pageViews: number; visitors: number; sessions: number; liveVisitors: number; pagesPerSession: number; bounceRate: number; newVisitors: number; returningVisitors: number; botViews: number; };
    comparison: { pageViews: number; visitors: number; sessions: number; };
    daily: AnalyticsDailyPoint[];
    hourly: Array<{ hour: number; value: number }>;
    topPages: AnalyticsMetric[];
    referrers: AnalyticsMetric[];
    campaigns: AnalyticsMetric[];
    devices: AnalyticsMetric[];
    browsers: AnalyticsMetric[];
    operatingSystems: AnalyticsMetric[];
    languages: AnalyticsMetric[];
    screenSizes: AnalyticsMetric[];
    recentVisits: AnalyticsRecentVisit[];
}

export const getAnalytics = (days: 7 | 30 | 90) =>
    adminApi.get<AnalyticsDashboardData>('/analytics', { params: { days } });

export type CommunityReport = {
    id: number;
    targetType: 'POST' | 'COMMENT';
    targetId: number;
    targetContent: string | null;
    reporterEmail: string;
    reason: 'SPAM' | 'ABUSE' | 'FALSE_INFORMATION' | 'OTHER';
    detail: string | null;
    status: 'PENDING' | 'RESOLVED' | 'DISMISSED';
    createdAt: string;
};

export const getAdminReports = async (status: 'PENDING' | 'RESOLVED' | 'DISMISSED' | 'ALL'): Promise<CommunityReport[]> =>
    (await adminApi.get<CommunityReport[]>('/reports', { params: { status } })).data;

export const updateAdminReportStatus = async (id: number, status: 'PENDING' | 'RESOLVED' | 'DISMISSED'): Promise<void> => {
    await adminApi.put(`/reports/${id}/status`, { status });
};

// =========================
// JWT 자동 추가
// =========================

adminApi.interceptors.request.use(
    (config) => {
        const token =
            localStorage.getItem(
                'token'
            );

        if(token) {
            config.headers.Authorization =
                `Bearer ${token}`;
        }

        return config;
    }
);

adminApi.interceptors.response.use(
    (response) => response,
    (error) => {
        recoverFromAdminAuthFailure(error?.response?.status);
        return Promise.reject(error);
    }
);

// =========================
// 게시글 관리
// =========================

// 게시글 조회
export const getAdminPosts = async (
    page: number,
    keyword: string
) => {
    return await adminApi.get(
        '/posts',
        {
            params: {
                page,
                size: 5,
                keyword
            }
        }
    );
};

// 게시글 삭제
export const deleteAdminPost = async (
    postId: number
) => {
    return await adminApi.delete(
        `/posts/${postId}`
    );
};

// 게시글 수정
export const updateAdminPost = async (
    postId: number,
    content: string
) => {
    return await adminApi.put(
        `/posts/${postId}`,
        {
            content
        }
    );
};

// =========================
// 댓글 관리
// =========================

// 특정 게시글 댓글 조회
export const getAdminComments = (
    postId: number
) => {
    return adminApi.get(
        `/posts/${postId}/comments`
    );
};

// 댓글 삭제
export const deleteAdminComment = (
    commentId: number
) => {
    return adminApi.delete(
        `/comments/${commentId}`
    );
};

// 전체 댓글 조회
export const getAllAdminComments = (
    page: number,
    keyword: string
) => {
    return adminApi.get(
        `/comments?page=${page}&size=5&keyword=${keyword}`
    );
};

// =========================
// 회원 관리
// =========================

// 회원 조회
export const getUsers = (
    page: number,
    keyword: string
) => {
    return adminApi.get(
        `/users?page=${page}&keyword=${keyword}`
    );
};

// 회원 삭제
export const deleteUser = (
    userId: number
) => {
    return adminApi.delete(
        `/users/${userId}`
    );
};

// 회원 수정
export const updateUser = (
    userId: number,
    nickname: string,
    role: string
) => {
    return adminApi.put(
        `/users/${userId}`,
        {
            nickname,
            role
        }
    );
};

// =========================
// 공지사항 관리
// =========================

export const getNotices = () => {
    return adminApi.get('/notice');
};

export const getNotice = (id: number) => {
    return adminApi.get(`/notice/${id}`);
};

export const insertNotice = (data: {
    title: string;
    content: string;
    writerId: number;
}) => {
    return adminApi.post('/notice', data);
};

export const updateNotice = (id: number, data: {
    title: string;
    content: string;
    writerId: number;
}) => {
    return adminApi.put(`/notice/${id}`, data);
};

export const deleteNotice = (id: number) => {
    return adminApi.delete(`/notice/${id}`);
};

// =========================
// 앱 게시물 관리
// =========================

export const getAppContents = () => {
    return adminApi.get('/app-contents');
};

export const insertAppContent = (data: {
    title: string;
    imageUrl: string;
    youtubeUrl: string;
    category: string;
    content: string;
}) => {
    return adminApi.post('/app-contents', data);
};

export const updateAppContent = (id: number, data: {
    title: string;
    imageUrl: string;
    youtubeUrl: string;
    category: string;
    content: string;
}) => {
    return adminApi.put(`/app-contents/${id}`, data);
};

export const deleteAppContent = (id: number) => {
    return adminApi.delete(`/app-contents/${id}`);
};

export const updateAppContentTitle = (id: number, title: string) => {
    return adminApi.put(`/app-contents/${id}/title`, { title });
};

export const getShowcaseItems = () => {
    return adminApi.get('/showcase');
};

export const insertShowcaseItem = (data: {
    title: string;
    imageUrl: string;
    content: string;
}) => {
    return adminApi.post('/showcase', data);
};

export const updateShowcaseItem = (id: number, data: {
    title: string;
    imageUrl: string;
    content: string;
}) => {
    return adminApi.put(`/showcase/${id}`, data);
};

export const deleteShowcaseItem = (id: number) => {
    return adminApi.delete(`/showcase/${id}`);
};

export const getShowcaseComments = (showcaseId: number) => {
    return adminApi.get(`/showcase/${showcaseId}/comments`);
};

export const deleteShowcaseComment = (showcaseId: number, commentId: number) => {
    return adminApi.delete(`/showcase/${showcaseId}/comments/${commentId}`);
};

export const createShowcaseComment = (showcaseId: number, content: string) => {
    return adminApi.post(`/showcase/${showcaseId}/comments`, { content, nickname: '' });
};



// =========================
// 앱 상단 관리
// =========================

export const getTopVideos = () => {
    return adminApi.get('/top-video');
};

export const insertTopVideo = (data: { youtubeUrl: string }) => {
    return adminApi.post('/top-video', data);
};

export const deleteTopVideo = (id: number) => {
    return adminApi.delete(`/top-video/${id}`);
};

export const updateTopVideo = (id: number, youtubeUrl: string) => {
    return adminApi.put(`/top-video/${id}`, { youtubeUrl });
};

export const updateTopVideoTitle = (id: number, title: string) => {
    return adminApi.put(`/top-video/${id}/title`, { title });
};

// =========================
// 쇼츠 관리
// =========================

export const getShorts = () => {
    return adminApi.get('/shorts');
};

export const insertShorts = (youtubeUrl: string) => {
    return adminApi.post('/shorts', { youtubeUrl });
};

export const updateShortsTitle = (id: number, title: string) => {
    return adminApi.put(`/shorts/${id}/title`, { title });
};

export const deleteShorts = (id: number) => {
    return adminApi.delete(`/shorts/${id}`);
};

// =========================
// 이야기 관리
// =========================

export interface AppStoryRequest {
    title: string;
    content: string;
}

export const getStories = () => {
    return adminApi.get('/stories');
};

export const getStory = (id: number) => {
    return adminApi.get(`/stories/${id}`);
};

export const insertStory = (data: AppStoryRequest) => {
    return adminApi.post('/stories', data);
};

export const updateStory = (id: number, data: AppStoryRequest) => {
    return adminApi.put(`/stories/${id}`, data);
};

export const deleteStory = (id: number) => {
    return adminApi.delete(`/stories/${id}`);
};

export const getStoryComments = (storyId: number) => {
    return adminApi.get(`/stories/${storyId}/comments`);
};

export const insertStoryComment = (storyId: number, nickname: string, content: string) => {
    return adminApi.post('/stories/comments', { storyId, nickname, content });
};

export const deleteStoryComment = (commentId: number) => {
    return adminApi.delete(`/stories/comments/${commentId}`);
};

// =========================
// 앱 댓글 관리
// =========================

export const getAllStoryComments = (page: number, keyword: string) => {
    return adminApi.get(`/stories/comments?page=${page}&size=10&keyword=${keyword}`);
};

export const getAdminCategories = (keyword: string) => {
    return adminApi.get(`/api/categories?keyword=${keyword}`);
};

export const insertCategory = (name: string) => {
    return adminApi.post('/api/categories', { name });
};

export const updateCategory = (id: number, name: string) => {
    return adminApi.put(`/api/categories/${id}`, { name });
};

export const deleteCategory = (id: number) => {
    return adminApi.delete(`/api/categories/${id}`);
};

export default adminApi;
